<!--
	@component
	Manages per-user news badge state (seen counts, click dismissals).

  > FIXME: migrate away from store to $state

	### How it works
	- Fetches user preferences from `/api/preference/get/news-badges` on mount
	- Merges with `newsConfig` defaults via `enforce_news_template`
	- Mirrors state to `localStorage` (`news-badge`) and the DB
	- Listens for `newsUpdate` window events (dispatched by vanilla JS in `utils.svelte.ts`)
	  and increments `seen` counts up to `max_exposures`

	### Context provided
	- `user_state` — `Writable<NewsState | null>`, per-item `{ clicked, seen }`
	- `news_config` — `Writable<NewsConfig>`, static config (expiry, max exposures, tooltips)

	### Write coalescing
	Concurrent writes are queued — only one `setUserPreference` call is in flight at a time.

	@see {@link ./news-config.ts} for the news item configuration
	`@see` {`@link` ../utils/utils.svelte.ts} for badge rendering and `newsUpdate` dispatch
	@see {@link ./news.types.ts} for `NewsState`, `NewsConfig`
-->
<script lang="ts">
	import { onMount, setContext } from 'svelte'
	import { writable, type Writable } from 'svelte/store'
	import { getUserPreference, setUserPreference } from '$utils/user_preferences'
	import { enforce_news_template } from './utils'
	import { newsConfig } from './news-config'
	import { notificationsFeed } from './notifications-feed-mock.svelte'
	import type { NewsItemState, NewsState } from './news.types'

	const DB_TABLE = 'news-badges'

	let is_writing = false
	let write_queued = false
	const user_state: Writable<NewsState | null> = writable(null)
	setContext('user_state', user_state)

	// TODO: should be remote config (DB, JSON file, etc.)
	const news_config = writable(newsConfig)

	const news_items = Object.keys($news_config)
	setContext('news_config', news_config)

	setContext('notifications', notificationsFeed)

	const zero_user_state: NewsState = {
		news: {
			...Object.keys($news_config).reduce<Record<string, NewsItemState>>((acc, item_id) => {
				acc[item_id] = {
					clicked: false,
					seen: 0
				}
				return acc
			}, {})
		}
	}

	onMount(async () => {
		/** DRAFT: Fallback event listener - maybe for Sirius */
		// window.addEventListener('notification', (event) => console.log('received:', event.detail));

		// return () => {
		//   window.removeEventListener('notification', (event) => console.log('received:', event.detail));
		// };

		$user_state = await ensureLocalUIPrefs()
	})

	// make sure we end up with a valid state object
	async function ensureLocalUIPrefs() {
		const db_state = await getUserPreference<NewsState['news']>(DB_TABLE)

		let storage_state: NewsState

		if (db_state) {
			const valid_db_state = enforce_news_template(
				{ news: db_state },
				news_items,
				zero_user_state.news
			)
			storage_state = { ...zero_user_state, ...valid_db_state }
		} else {
			storage_state = zero_user_state
			// seed the DB so subsequent updates don't race to create the entry
			await setUserPreference(DB_TABLE, storage_state.news)
		}

		localStorage.setItem(DB_TABLE, JSON.stringify(storage_state))

		return storage_state
	}

	// if downstream event happens (view, click),
	// update state, update localStorage, and send to DB
	async function updateUIPrefs(tag_name: string) {
		if (!$user_state?.news?.[tag_name]) return

		const item = $user_state.news[tag_name]
		const config = $news_config[tag_name]
		if (item.seen != null && config?.max_exposures != null && item.seen < config.max_exposures) {
			item.seen++
		}
		localStorage.setItem(DB_TABLE, JSON.stringify($user_state))
		await persistState()
	}

	async function persistState() {
		if (is_writing) {
			write_queued = true
			return
		}

		is_writing = true

		await setUserPreference(DB_TABLE, $user_state!.news)

		if (write_queued) {
			write_queued = false
			await persistState()
		}

		is_writing = false
	}

	// global event listener; needs newsItemId
	async function handleNewsUpdate(event: CustomEvent<{ newsItemId: string }>) {
		const newsItemId = event.detail.newsItemId

		if (newsItemId && $news_config[newsItemId]) {
			await updateUIPrefs(newsItemId)
		}
	}
</script>

<svelte:window on:newsUpdate={handleNewsUpdate} />

<slot />
